// app/frontend/pages/Admin/Ads/AdsIndex.tsx
import React, { useState } from 'react';
import { usePage, router } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import {
  Container,
  Title,
  Button,
  Group,
  TextInput,
  Select,
  Table,
  Badge,
  ActionIcon,
  Menu,
  Image,
  Text,
  Box,
  Stack,
  Paper,
  Flex,
  Switch,
  Tooltip,
  Checkbox,
  Modal,
  Card,
  Grid,
  Tabs,
  Loader,
} from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconDots,
  IconEye,
  IconToggleLeft,
  IconToggleRight,
  IconChartBar,
  IconCopy,
  IconDownload,
  IconFilter,
} from '@tabler/icons-react';
import { showNotification, Notifications } from '@mantine/notifications';

interface Ad {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  active: boolean;
  start_date?: string;
  end_date?: string;
  priority: number;
  ad_type: 'card' | 'banner' | 'popup';
  display_frequency: number;
  click_count: number;
  impression_count: number;
  click_through_rate?: number; // Make this optional
  created_at: string;
  updated_at: string;
  currently_active: boolean;
}

interface AdType {
  value: string;
  label: string;
}

interface Filters {
  search: string;
  ad_type: string;
  status: string;
}

interface Pagination {
  page: number;
  items: number;
  pages: number;
  count: number;
}

interface AdsIndexProps extends PageProps {
  ads: Ad[];
  pagy: Pagination;
  filters: Filters;
  ad_types: AdType[];
}

export default function AdsIndex() {
  const { ads, pagy, filters, ad_types } = usePage<AdsIndexProps>().props;

  // Debug: Log the received data
  // console.log('🐛 All ads received:', ads);
  // console.log('🐛 First ad data received:', ads[0]);
  // console.log(
  //   '🐛 First ad analytics:',
  //   ads[0]
  //     ? {
  //         click_count: ads[0].click_count,
  //         impression_count: ads[0].impression_count,
  //         click_through_rate: ads[0].click_through_rate,
  //       }
  //     : 'No ads',
  // );

  // State management
  const [search, setSearch] = useState(filters.search || '');
  const [selectedAdType, setSelectedAdType] = useState(filters.ad_type || '');
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '');
  const [selectedAds, setSelectedAds] = useState<number[]>([]);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  // Computed values
  const allSelected = selectedAds.length === ads.length;
  const indeterminate = selectedAds.length > 0 && !allSelected;

  // Filter ads by tab
  const filterAdsByTab = (ads: Ad[], tab: string) => {
    switch (tab) {
      case 'card':
        return ads.filter((ad) => ad.ad_type === 'card');
      case 'banner':
        return ads.filter((ad) => ad.ad_type === 'banner');
      case 'popup':
        return ads.filter((ad) => ad.ad_type === 'popup');
      case 'active':
        return ads.filter((ad) => ad.currently_active);
      case 'inactive':
        return ads.filter((ad) => !ad.currently_active);
      default:
        return ads;
    }
  };

  const filteredAds = filterAdsByTab(ads, activeTab);

  // Event handlers
  const handleSearch = () => {
    setIsLoading(true);
    router.visit('/admin/ads', {
      data: {
        search,
        ad_type: selectedAdType,
        status: selectedStatus,
      },
      preserveState: true,
      onFinish: () => setIsLoading(false),
    });
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedAdType('');
    setSelectedStatus('');
    setIsLoading(true);
    router.visit('/admin/ads', {
      preserveState: true,
      onFinish: () => setIsLoading(false),
    });
  };

  const handleToggleActive = async (ad: Ad) => {
    try {
      const response = await fetch(`/admin/ads/${ad.id}/toggle_active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.success) {
        showNotification({
          title: 'Success',
          message: data.message,
          color: 'green',
        });

        // Refresh the page data
        router.reload({ only: ['ads'] });
      } else {
        throw new Error('Failed to toggle ad status');
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to update ad status',
        color: 'red',
      });
    }
  };

  const handleDeleteAd = async () => {
    if (!adToDelete) return;

    try {
      const response = await fetch(`/admin/ads/${adToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.success) {
        showNotification({
          title: 'Success',
          message: data.message,
          color: 'green',
        });

        setDeleteModalOpened(false);
        setAdToDelete(null);
        router.reload({ only: ['ads'] });
      } else {
        throw new Error('Failed to delete ad');
      }
    } catch (error) {
      showNotification({
        title: 'Error',
        message: 'Failed to delete ad',
        color: 'red',
      });
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedAds([]);
    } else {
      setSelectedAds(filteredAds.map((ad) => ad.id));
    }
  };

  const handleSelectAd = (adId: number) => {
    setSelectedAds((prev) => (prev.includes(adId) ? prev.filter((id) => id !== adId) : [...prev, adId]));
  };

  const handleBulkActivate = async (activate: boolean) => {
    // Implementation for bulk activate/deactivate
    console.log(`Bulk ${activate ? 'activate' : 'deactivate'} ads:`, selectedAds);
    // TODO: Implement bulk operations endpoint
  };

  const getStatusBadge = (ad: Ad) => {
    if (!ad.active) {
      return (
        <Badge color="gray" variant="filled">
          Inactive
        </Badge>
      );
    }
    if (ad.currently_active) {
      return (
        <Badge color="green" variant="filled">
          Active
        </Badge>
      );
    }
    if (ad.start_date && new Date(ad.start_date) > new Date()) {
      return (
        <Badge color="blue" variant="filled">
          Scheduled
        </Badge>
      );
    }
    if (ad.end_date && new Date(ad.end_date) < new Date()) {
      return (
        <Badge color="orange" variant="filled">
          Expired
        </Badge>
      );
    }
    return (
      <Badge color="gray" variant="filled">
        Inactive
      </Badge>
    );
  };

  const getAdTypeBadge = (type: string) => {
    const colors = {
      card: 'blue',
      banner: 'orange',
      popup: 'purple',
    };
    return (
      <Badge color={colors[type as keyof typeof colors]} variant="light">
        {type}
      </Badge>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatPercentage = (value: number | undefined | null) => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  return (
    <Container size="xl" py="xl">
      <Notifications position="top-right" />

      {/* Page Title and Actions */}
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={1} className="rubik" c="#f2f2f2">
              Ad Management
            </Title>
            <Text size="sm" c="dimmed">
              Manage your advertisement campaigns
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => router.visit('/admin/ads/new')}
            color="#EA580C"
            size="md"
          >
            Create New Ad
          </Button>
        </Group>

        {/* Filters */}
        <Paper p="md" withBorder style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
          <Stack gap="md">
            <Group>
              <TextInput
                placeholder="Search ads..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Select
                placeholder="Ad Type"
                value={selectedAdType}
                onChange={(value) => setSelectedAdType(value || '')}
                data={[{ value: '', label: 'All Types' }, ...ad_types]}
                clearable
              />
              <Select
                placeholder="Status"
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value || '')}
                data={[
                  { value: '', label: 'All Statuses' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'scheduled', label: 'Scheduled' },
                ]}
                clearable
              />
              <Button onClick={handleSearch} disabled={isLoading}>
                {isLoading ? <Loader size="xs" /> : 'Filter'}
              </Button>
              <Button variant="light" onClick={handleClearFilters}>
                Clear
              </Button>
            </Group>

            {/* Bulk Actions */}
            {selectedAds.length > 0 && (
              <Group>
                <Text size="sm">
                  {selectedAds.length} ad{selectedAds.length !== 1 ? 's' : ''} selected
                </Text>
                <Button size="xs" variant="light" onClick={() => handleBulkActivate(true)}>
                  Activate Selected
                </Button>
                <Button size="xs" variant="light" color="gray" onClick={() => handleBulkActivate(false)}>
                  Deactivate Selected
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'all')}>
          <Tabs.List>
            <Tabs.Tab value="all">All ({ads.length})</Tabs.Tab>
            <Tabs.Tab value="card">Card Ads ({ads.filter((ad) => ad.ad_type === 'card').length})</Tabs.Tab>
            <Tabs.Tab value="banner">Banner Ads ({ads.filter((ad) => ad.ad_type === 'banner').length})</Tabs.Tab>
            <Tabs.Tab value="popup">Popup Ads ({ads.filter((ad) => ad.ad_type === 'popup').length})</Tabs.Tab>
            <Tabs.Tab value="active">Active ({ads.filter((ad) => ad.currently_active).length})</Tabs.Tab>
            <Tabs.Tab value="inactive">Inactive ({ads.filter((ad) => !ad.currently_active).length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={activeTab} pt="md">
            {/* Ads Table */}
            <Paper withBorder style={{ backgroundColor: '#171717', cursor: 'pointer' }}>
              <Table
                striped
                highlightOnHover
                styles={{
                  table: {
                    width: '100%',
                    tableLayout: 'fixed',
                  },
                  td: {
                    padding: '8px 8px',
                    verticalAlign: 'middle',
                    backgroundColor: '#171717',
                    color: '#f2f2f2',
                  },
                  th: {
                    padding: '12px 8px',
                    color: '#f2f2f2',
                  },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <Checkbox checked={allSelected} indeterminate={indeterminate} onChange={handleSelectAll} />
                    </Table.Th>
                    <Table.Th>Preview</Table.Th>
                    <Table.Th>Title</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Performance</Table.Th>
                    <Table.Th>Dates</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredAds.map((ad) => (
                    <Table.Tr key={ad.id}>
                      <Table.Td>
                        <Checkbox checked={selectedAds.includes(ad.id)} onChange={() => handleSelectAd(ad.id)} />
                      </Table.Td>
                      <Table.Td>
                        <Box style={{ width: 60, height: 40 }}>
                          {ad.banner_url ? (
                            <Image
                              src={ad.banner_url}
                              alt={ad.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              radius="sm"
                            />
                          ) : (
                            <Box
                              style={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#f1f3f4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                              }}
                            >
                              <Text size="xs" c="dimmed">
                                No Image
                              </Text>
                            </Box>
                          )}
                        </Box>
                      </Table.Td>
                      <Table.Td>
                        <div>
                          <Text fw={500} size="sm">
                            {ad.title}
                          </Text>
                          {ad.description && (
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {ad.description}
                            </Text>
                          )}
                        </div>
                      </Table.Td>
                      <Table.Td>{getAdTypeBadge(ad.ad_type)}</Table.Td>
                      <Table.Td>{getStatusBadge(ad)}</Table.Td>
                      <Table.Td>
                        <Stack gap="xs">
                          <Text size="xs">
                            Clicks: <strong>{ad.click_count}</strong>
                          </Text>
                          <Text size="xs">
                            Impressions: <strong>{ad.impression_count}</strong>
                          </Text>
                          <Text size="xs">
                            CTR: <strong>{formatPercentage(ad.click_through_rate)}</strong>
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap="xs">
                          <Text size="xs">Start: {formatDate(ad.start_date)}</Text>
                          <Text size="xs">End: {formatDate(ad.end_date)}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Tooltip label={ad.currently_active ? 'Deactivate' : 'Activate'}>
                            <ActionIcon
                              variant="light"
                              color={ad.currently_active ? 'orange' : 'green'}
                              onClick={() => handleToggleActive(ad)}
                            >
                              {ad.currently_active ? <IconToggleRight size={16} /> : <IconToggleLeft size={16} />}
                            </ActionIcon>
                          </Tooltip>

                          <Menu withArrow>
                            <Menu.Target>
                              <ActionIcon variant="light">
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown
                              style={{
                                backgroundColor: '#262626',
                                borderColor: '#404040',
                                color: '#f2f2f2',
                              }}
                            >
                              <Menu.Item
                                leftSection={<IconEye size={14} />}
                                onClick={() => router.visit(`/admin/ads/${ad.id}`)}
                              >
                                View Details
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconEdit size={14} />}
                                onClick={() => router.visit(`/admin/ads/${ad.id}/edit`)}
                              >
                                Edit
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconChartBar size={14} />}
                                onClick={() => console.log('View analytics for', ad.id)}
                              >
                                Analytics
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconCopy size={14} />}
                                onClick={() => console.log('Duplicate ad', ad.id)}
                              >
                                Duplicate
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                leftSection={<IconTrash size={14} />}
                                color="red"
                                onClick={() => {
                                  setAdToDelete(ad);
                                  setDeleteModalOpened(true);
                                }}
                              >
                                Delete
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {filteredAds.length === 0 && (
                <Box p="xl" style={{ textAlign: 'center' }}>
                  <Text size="lg" c="dimmed">
                    No ads found
                  </Text>
                  <Text size="sm" c="dimmed" mt="xs">
                    {activeTab === 'all'
                      ? 'Create your first ad to get started'
                      : `No ${activeTab} ads match your current filters`}
                  </Text>
                  {activeTab === 'all' && (
                    <Button
                      mt="md"
                      leftSection={<IconPlus size={16} />}
                      onClick={() => router.visit('/admin/ads/new')}
                      color="#EA580C"
                    >
                      Create Your First Ad
                    </Button>
                  )}
                </Box>
              )}
            </Paper>
          </Tabs.Panel>
        </Tabs>

        {/* Pagination */}
        {pagy.pages > 1 && (
          <Group justify="center">
            {/* Add pagination component here */}
            <Text size="sm">
              Page {pagy.page} of {pagy.pages} ({pagy.count} total ads)
            </Text>
          </Group>
        )}

        {/* Delete Confirmation Modal */}
        <Modal opened={deleteModalOpened} onClose={() => setDeleteModalOpened(false)} title="Delete Ad" centered>
          <Stack>
            <Text>Are you sure you want to delete the ad "{adToDelete?.title}"? This action cannot be undone.</Text>
            <Group justify="flex-end">
              <Button variant="light" onClick={() => setDeleteModalOpened(false)}>
                Cancel
              </Button>
              <Button color="red" onClick={handleDeleteAd}>
                Delete Ad
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}
